import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { NAVBAR_HEIGHT } from "constants/layout";
import { usePageTitle } from "hooks/usePageTitle";
import { useStateContext } from "contexts/ContextProvider";
import {
  commentCategoryLabel,
  formatTimestamp,
} from "helpers/publicVideoComments";
import {
  getVideoComments,
  getVideoLibraryItem,
  replyToVideoComment,
  restoreVideoComment,
  deleteVideoComment,
  setVideoCommentResolved,
} from "services/videoLibraryApi";
import { OVERLAY_NAMES } from "constants/Overlays";
import CommentActionsMenu from "components/CommentActionsMenu";

const PUBLIC_VIDEO_ORIGIN = "https://videos.communicationessentialsllc.com";
const IS_DEV = process.env.NODE_ENV !== "production";

// Both visibilities share the public video host. An Internal Only link still
// needs a session on that origin, so the page offers sign-in when it 404s.
const videoUrlFor = (video) => {
  if (!video || video.status !== "published" || !video.publicId) return null;
  const encodedId = encodeURIComponent(video.publicId);
  if (IS_DEV) return `${window.location.origin}/videos/${encodedId}`;
  return `${PUBLIC_VIDEO_ORIGIN}/v/${encodedId}`;
};

const STATUS_CHIP = {
  published: { label: "Published", color: "success" },
  draft: { label: "Draft", color: "default" },
  unpublished: { label: "Unpublished", color: "warning" },
};

// Mirrors ClientProfile's header buttons so these read like the rest of the portal.
const actionButtonSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  padding: "8px 16px",
  backgroundColor: theme.palette.primary.main,
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
  color: theme.palette.background.default,
  textTransform: "none",
  fontSize: "0.875rem",
  "& .MuiButton-startIcon": { marginRight: 0 },
  "&:hover": {
    backgroundColor: theme.palette.primary.buttonHoverSelected,
    color: theme.palette.background.default,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
  },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  },
});

// Same card surface as ClientProfile / LinguistProfile.
const ProfileCard = ({ theme, isMobile, children }) => (
  <Box
    sx={{
      bgcolor: theme.palette.background.default,
      borderRadius: "12px",
      border: `1px solid ${theme.palette.primary.border2}`,
      p: isMobile ? "0.75rem" : "1.25rem",
      m: "0.25rem",
      boxShadow: theme.shadows[3],
      minWidth: 0,
      width: "calc(100% - 0.5rem)",
    }}
  >
    {children}
  </Box>
);

const DetailRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="p">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const VideoProfile = () => {
  const { videoId } = useParams();
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:900px)");
  const { setOverlayName, setOverlayData, showSnackbar } = useStateContext();

  const [video, setVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState("");

  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [filter, setFilter] = useState("open");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [replyOpenFor, setReplyOpenFor] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busyKey, setBusyKey] = useState("");

  usePageTitle(video?.title ? `${video.title} | Video Library` : "Video Library");

  const loadVideo = useCallback(async () => {
    setVideoLoading(true);
    setVideoError("");
    try {
      const response = await getVideoLibraryItem(videoId);
      setVideo(response.data?.video || null);
    } catch {
      setVideoError("This video could not be loaded.");
    } finally {
      setVideoLoading(false);
    }
  }, [videoId]);

  const loadComments = useCallback(
    async (nextFilter = "open") => {
      setCommentsLoading(true);
      setCommentsError("");
      try {
        const response =
          nextFilter === "deleted"
            ? await getVideoComments({ videoId, deletedOnly: true })
            : await getVideoComments({
                videoId,
                resolved: nextFilter === "done",
              });
        const list = response.data?.comments;
        setComments(Array.isArray(list) ? list : []);
      } catch {
        setCommentsError("Comments could not be loaded.");
      } finally {
        setCommentsLoading(false);
      }
    },
    [videoId],
  );

  useEffect(() => {
    loadVideo();
  }, [loadVideo]);

  useEffect(() => {
    loadComments(filter);
  }, [filter, loadComments]);

  // The API returns one flat list; group it so replies render inside their parent
  // thread rather than as loose cards of their own.
  const threads = useMemo(() => {
    const roots = [];
    const repliesByParent = new Map();
    const orphanReplies = [];

    comments.forEach((comment) => {
      if (comment.parentCommentId == null) {
        roots.push(comment);
      }
    });
    const rootIds = new Set(roots.map((root) => root.id));

    comments.forEach((comment) => {
      if (comment.parentCommentId == null) return;
      if (!rootIds.has(comment.parentCommentId)) {
        orphanReplies.push(comment);
        return;
      }
      const list = repliesByParent.get(comment.parentCommentId) || [];
      list.push(comment);
      repliesByParent.set(comment.parentCommentId, list);
    });

    return { roots, repliesByParent, orphanReplies };
  }, [comments]);

  const publicUrl = videoUrlFor(video);

  const handleCopy = () => {
    if (!publicUrl) return;
    navigator.clipboard
      .writeText(publicUrl)
      .then(() => showSnackbar("Link copied to clipboard.", theme, "success"))
      .catch(() => showSnackbar("Failed to copy the link.", theme, "error"));
  };

  const openEdit = () => {
    setOverlayData({
      Action: OVERLAY_NAMES.VIDEO_ACTION_EDIT,
      video,
      onVideosSaved: loadVideo,
    });
    setOverlayName(OVERLAY_NAMES.MANAGEMENT_VIDEO_NEW_EDIT);
  };

  const withBusy = async (comment, task, failureMessage) => {
    const key = `comment-${comment.id}`;
    setBusyKey(key);
    setCommentsError("");
    try {
      await task();
      // Keeps the sidebar's open-comment badge honest without a page change.
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("videoCommentsChanged"));
      }
      await loadComments(filter);
    } catch (error) {
      setCommentsError(error?.response?.data?.error || failureMessage);
    } finally {
      setBusyKey("");
    }
  };

  const handleResolve = (comment, resolved) =>
    withBusy(
      comment,
      () => setVideoCommentResolved(comment.id, resolved),
      "The comment could not be updated.",
    );

  const handleRestore = (comment) =>
    withBusy(
      comment,
      () => restoreVideoComment(comment.id),
      "The comment could not be restored.",
    );

  const confirmAdminDelete = async () => {
    const comment = pendingDelete;
    if (!comment) return;
    setPendingDelete(null);
    await withBusy(
      comment,
      () => deleteVideoComment(comment.id),
      "The comment could not be deleted.",
    );
  };

  const handleReply = async (comment) => {
    const draft = (replyDrafts[comment.id] || "").trim();
    if (!draft) return;
    await withBusy(
      comment,
      async () => {
        await replyToVideoComment(comment.id, draft);
        setReplyDrafts((current) => ({ ...current, [comment.id]: "" }));
        setReplyOpenFor(null);
        showSnackbar("Reply posted.", theme, "success");
      },
      "The reply could not be posted.",
    );
  };

  // One card body, used for both a thread's root comment and its replies.
  const renderCommentBody = (comment, { isReply }) => {
    const isBusy = busyKey === `comment-${comment.id}`;
    return (
      <>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="subtitle2" fontWeight={700}>
            {comment.name}
          </Typography>
          {comment.email && (
            <Typography
              component="a"
              href={`mailto:${comment.email}`}
              variant="body2"
              color="text.secondary"
              sx={{ textDecoration: "none" }}
            >
              {comment.email}
            </Typography>
          )}
          <Chip size="small" label={commentCategoryLabel(comment.category)} />
          {comment.languageCode && comment.languageCode !== "en" && (
            <Chip
              size="small"
              variant="outlined"
              label={comment.languageCode.toUpperCase()}
            />
          )}
          {Number.isFinite(comment.timestampSeconds) && (
            <Chip
              size="small"
              variant="outlined"
              label={formatTimestamp(comment.timestampSeconds)}
            />
          )}
          {isReply && <Chip size="small" variant="outlined" label="Reply" />}
          {comment.isAdmin && <Chip size="small" color="primary" label="Team" />}
          {comment.resolvedAt && (
            <Chip size="small" color="success" label="Done" />
          )}
          {filter === "deleted" && comment.deletedAt && (
            <Chip
              size="small"
              label={
                comment.deletedBy === "commenter"
                  ? "Deleted by author"
                  : "Deleted by admin"
              }
            />
          )}
          {filter === "deleted" && !comment.deletedAt && (
            <Chip size="small" variant="outlined" label="Shown for context" />
          )}
          <CommentActionsMenu
            comment={comment}
            isBusy={isBusy}
            onResolve={() => handleResolve(comment, !comment.resolvedAt)}
            onRestore={() => handleRestore(comment)}
            onDelete={() => setPendingDelete(comment)}
          />
        </Stack>

        <Typography
          variant="body2"
          sx={{ mt: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
        >
          {comment.body}
        </Typography>

        {/* The done / restore / delete actions live in the ⋮ menu above; replies
            are part of a thread and only offer Delete. */}
      </>
    );
  };

  const replyCardSx = {
    bgcolor: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "10px",
    p: 1.25,
    minWidth: 0,
  };

  const shellSx = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
    "@supports (height: 100dvh)": {
      height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
    },
    minHeight: 0,
  };
  const shellPadding = isMobile ? "0.5rem 0.5rem 0 0.5rem" : "1rem 1rem 0 1rem";

  if (videoLoading) {
    return (
      <Box p={shellPadding} bgcolor={theme.palette.background.alt} sx={shellSx}>
        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ flex: 1 }}>
          <CircularProgress size={26} />
          <Typography>Loading video…</Typography>
        </Stack>
      </Box>
    );
  }

  if (videoError || !video) {
    return (
      <Box p={shellPadding} bgcolor={theme.palette.background.alt} sx={shellSx}>
        <Box sx={{ px: 1, pt: 2 }}>
          <Alert severity="error">
            {videoError || "This video could not be loaded."}
          </Alert>
        </Box>
      </Box>
    );
  }

  const statusChip = STATUS_CHIP[video.status] || STATUS_CHIP.draft;
  const actionSx = {
    ...actionButtonSx(theme),
    flex: isMobile ? "1 1 100%" : "0 0 auto",
  };
  const hasComments = threads.roots.length > 0 || threads.orphanReplies.length > 0;

  return (
    <Box p={shellPadding} bgcolor={theme.palette.background.alt} sx={shellSx}>
      <Box sx={{ flexShrink: 0 }}>
        <Box
          display="flex"
          gap={isMobile ? 1 : 2}
          alignItems="center"
          flexWrap="wrap"
          justifyContent="flex-end"
          sx={{ pr: 1, pb: 1 }}
        >
          <Button onClick={openEdit} startIcon={<EditRoundedIcon />} sx={actionSx}>
            Edit Video
          </Button>
          <Button
            component="a"
            href={publicUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!publicUrl}
            startIcon={<OpenInNewRoundedIcon />}
            sx={actionSx}
          >
            Open Public Page
          </Button>
          <Button
            onClick={handleCopy}
            disabled={!publicUrl}
            startIcon={<ContentCopyRoundedIcon />}
            sx={actionSx}
          >
            Copy Link
          </Button>
        </Box>
      </Box>

      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
          pb: 1,
          px: 1,
          pt: isMobile ? 1 : 2,
        }}
      >
        <Stack spacing={2} sx={{ width: "100%" }}>
          <ProfileCard theme={theme} isMobile={isMobile}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
            >
              <Typography component="h1" variant="h5" sx={{ fontWeight: 600 }}>
                {video.title}
              </Typography>
              <Chip size="small" color={statusChip.color} label={statusChip.label} />
              <Chip
                size="small"
                variant="outlined"
                label={video.visibility === "public" ? "Public" : "Internal Only"}
              />
            </Stack>
            {video.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {video.description}
              </Typography>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                mt: 2,
              }}
            >
              <DetailRow label="Public ID" value={video.publicId} />
              <DetailRow
                label="Length"
                value={
                  Number.isFinite(video.durationSeconds)
                    ? formatTimestamp(video.durationSeconds)
                    : "Auto-detected from Synthesia"
                }
              />
              <DetailRow label="Synthesia video ID" value={video.synthesiaVideoId} />
            </Box>
            {publicUrl && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1.5 }}
              >
                Public page:{" "}
                <Link href={publicUrl} target="_blank" rel="noopener noreferrer">
                  {publicUrl}
                </Link>
              </Typography>
            )}
          </ProfileCard>

          <ProfileCard theme={theme} isMobile={isMobile}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Languages
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Box
                component="table"
                sx={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
              >
                <Box component="thead">
                  <Box component="tr">
                    {["Language", "Code", "Length", "Video ID"].map((heading) => (
                      <Box
                        component="th"
                        key={heading}
                        sx={{
                          textAlign: "left",
                          py: 0.75,
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          color: theme.palette.text.secondary,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {heading}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {(video.languages || []).map((language) => (
                    <Box component="tr" key={language.code}>
                      <Box component="td" sx={{ py: 0.75 }}>
                        {language.label}
                      </Box>
                      <Box component="td" sx={{ py: 0.75 }}>
                        {language.code.toUpperCase()}
                      </Box>
                      <Box component="td" sx={{ py: 0.75 }}>
                        {Number.isFinite(language.durationSeconds)
                          ? formatTimestamp(language.durationSeconds)
                          : "—"}
                      </Box>
                      <Box component="td" sx={{ py: 0.75, overflowWrap: "anywhere" }}>
                        {language.synthesiaVideoId || "Same as root"}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </ProfileCard>

          <ProfileCard theme={theme} isMobile={isMobile}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Comments
              </Typography>
              <Box sx={{ flex: 1 }} />
              {[
                { key: "open", label: "Open" },
                { key: "done", label: "Done" },
                { key: "deleted", label: "Deleted" },
              ].map((entry) => (
                <Button
                  key={entry.key}
                  size="small"
                  variant={filter === entry.key ? "contained" : "text"}
                  onClick={() => setFilter(entry.key)}
                  sx={{ textTransform: "none", borderRadius: "8px" }}
                >
                  {entry.label}
                </Button>
              ))}
            </Stack>

            {commentsLoading && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading comments…</Typography>
              </Stack>
            )}
            {commentsError && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {commentsError}
              </Alert>
            )}
            {!commentsLoading && !commentsError && !hasComments && (
              <Typography variant="body2" color="text.secondary">
                {filter === "deleted"
                  ? "No comments have been deleted."
                  : filter === "done"
                    ? "Nothing has been marked done yet."
                    : "No open comments on this video."}
              </Typography>
            )}

            {hasComments && (
              <Stack spacing={2}>
                {threads.roots.map((comment) => {
                  const replies = threads.repliesByParent.get(comment.id) || [];
                  const isBusy = busyKey === `comment-${comment.id}`;
                  const replyOpen = replyOpenFor === comment.id;
                  return (
                    <Box
                      key={comment.id}
                      sx={{
                        bgcolor: theme.palette.background.alt,
                        borderRadius: "12px",
                        p: 1.5,
                      }}
                    >
                      {renderCommentBody(comment, { isReply: false })}

                      {(replies.length > 0 || filter !== "deleted") && (
                        <Box
                          sx={{
                            mt: 1.5,
                            pl: { xs: 1.25, sm: 2 },
                            borderLeft: `3px solid ${theme.palette.divider}`,
                          }}
                        >
                          {replies.length > 0 && (
                            <Stack spacing={1}>
                              {replies.map((reply) => (
                                <Box key={reply.id} sx={replyCardSx}>
                                  {renderCommentBody(reply, { isReply: true })}
                                </Box>
                              ))}
                            </Stack>
                          )}

                          {filter !== "deleted" &&
                            (replyOpen ? (
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "flex-start" }}
                                sx={{ mt: replies.length > 0 ? 1 : 0 }}
                              >
                                <TextField
                                  size="small"
                                  fullWidth
                                  multiline
                                  maxRows={4}
                                  autoFocus
                                  placeholder="Reply as the team…"
                                  value={replyDrafts[comment.id] || ""}
                                  onChange={(event) =>
                                    setReplyDrafts((current) => ({
                                      ...current,
                                      [comment.id]: event.target.value,
                                    }))
                                  }
                                  inputProps={{
                                    "aria-label": `Reply to ${comment.name}`,
                                    maxLength: 1000,
                                  }}
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={
                                    isBusy || !(replyDrafts[comment.id] || "").trim()
                                  }
                                  onClick={() => handleReply(comment)}
                                  sx={{ textTransform: "none", borderRadius: "8px" }}
                                >
                                  Reply
                                </Button>
                                <Button
                                  size="small"
                                  onClick={() => setReplyOpenFor(null)}
                                  sx={{ textTransform: "none", borderRadius: "8px" }}
                                >
                                  Cancel
                                </Button>
                              </Stack>
                            ) : (
                              <Button
                                size="small"
                                startIcon={<SendRoundedIcon fontSize="small" />}
                                onClick={() => setReplyOpenFor(comment.id)}
                                sx={{
                                  mt: replies.length > 0 ? 1 : 0,
                                  textTransform: "none",
                                  borderRadius: "8px",
                                }}
                              >
                                Reply
                              </Button>
                            ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}

                {threads.orphanReplies.length > 0 && (
                  <Box>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <MoreVertRoundedIcon
                        fontSize="small"
                        sx={{ color: theme.palette.text.secondary }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Replies to comments not shown in this view
                      </Typography>
                    </Stack>
                    <Stack spacing={1}>
                      {threads.orphanReplies.map((reply) => (
                        <Box key={reply.id} sx={replyCardSx}>
                          {renderCommentBody(reply, { isReply: true })}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            )}
          </ProfileCard>
        </Stack>
      </Box>

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        maxWidth="xs"
        aria-labelledby="delete-comment-title"
      >
        <DialogTitle id="delete-comment-title">Delete comment</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The comment is hidden from the public page but kept, so you can restore
            it from the Deleted tab.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmAdminDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoProfile;
